import "innovate/App.css";
import { InteractionType } from "@azure/msal-browser";
import {
  MsalAuthenticationTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { ChakraProvider } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Loader from "innovate/components/common/Loader";
import Layout from "innovate/layout/Layout";
import Homepage from "innovate/pages/Homepage/Homepage";
import router from "innovate/routes";
import theme from "innovate/theme/index";
import { Suspense, useEffect, useRef } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  RouterProvider,
  Routes,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import "./datadog";
import { datadogRum } from "@datadog/browser-rum";
import {
  saveRedirectUrl,
  getRedirectUrl,
  clearRedirectUrl,
  isProtectedRoute,
} from "innovate/utils/authHelper";

const queryClient = new QueryClient();
const LoadingComponent = () => <Loader dH="90vh" dW="20vw" />;

/**
 * Unauthenticated Routes Component
 * Handles auto-redirect to login for protected routes using React Router's useLocation
 * This ensures client-side navigation triggers the auth check (not just page loads)
 */
const UnauthenticatedRoutes = () => {
  const location = useLocation();
  const { instance, accounts, inProgress } = useMsal();
  const loginTriggeredRef = useRef(false);
  const lastLoginPathRef = useRef<string | null>(null);

  const currentPath = location.pathname + location.search + location.hash;
  const isProtected = isProtectedRoute(currentPath);

  useEffect(() => {
    // Reset the "already triggered" flag when the URL changes (incl. query/hash)
    if (lastLoginPathRef.current !== currentPath) {
      loginTriggeredRef.current = false;
      lastLoginPathRef.current = currentPath;
    }

    // Don't trigger login if MSAL interaction is in progress
    if (inProgress !== "none") {
      return;
    }

    // Don't trigger login if user is authenticated
    if (accounts.length > 0) {
      return;
    }

    // Auto-redirect unauthenticated users on protected routes
    if (isProtected) {
      // Save as backup (in case MSAL fails)
      saveRedirectUrl(currentPath);
      
      // Trigger login redirect only once per unique URL
      if (!loginTriggeredRef.current) {
        loginTriggeredRef.current = true;
        
        instance.loginRedirect({
          scopes: ["openid", "profile", "email"],
          // Tell MSAL to return to this exact URL after login
          redirectStartPage: globalThis.location.origin + currentPath,
        }).catch((error) => {
          console.error("Login redirect failed:", error);
          loginTriggeredRef.current = false; // Allow retry
        });
      }
    }
  }, [location.pathname, location.search, location.hash, instance, accounts, inProgress, isProtected, currentPath]);

  return (
    <Routes>
      <Route path="/" element={<Homepage />} />
      {/* Redirect unknown routes to homepage if not protected */}
      {!isProtected && <Route path="*" element={<Navigate to="/" />} />}
      {/* Show loading while login is being triggered for protected routes */}
      {isProtected && <Route path="*" element={<LoadingComponent />} />}
    </Routes>
  );
};

/**
 * Authenticated App Component
 * Handles post-authentication redirect to preserved deep link
 * 
 * NOTE: MSAL's redirectStartPage should handle most redirects.
 * This is a fallback for edge cases (e.g., user closed tab during login).
 */
const AuthenticatedApp = () => {
  const { accounts } = useMsal();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    // Set Datadog user when accounts are available
    if (accounts.length > 0) {
      const user = accounts[0];
      if (user) {
        datadogRum.setUser({
          id: user.homeAccountId,
          email: user.username,
        });
      }
    }

    // Fallback redirect (only if MSAL didn't handle it)
    const savedUrl = getRedirectUrl();
    const currentPath = globalThis.location.pathname;
    
    // Only redirect if:
    // 1. We have a saved URL
    // 2. We're still on homepage (MSAL redirect didn't work)
    // 3. Haven't redirected yet
    if (savedUrl && currentPath === '/' && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      clearRedirectUrl();
      
      // Small delay to ensure app is fully initialized
      setTimeout(() => {
        router.navigate(savedUrl);
      }, 100);
    } else if (savedUrl && currentPath !== '/') {
      // Already on the correct page, just clear the saved URL
      clearRedirectUrl();
    }
  }, [accounts]);

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<Loader dH="90vh" dW="20vw" />}>
        <Layout>
          <RouterProvider router={router} />
        </Layout>
      </Suspense>
    </QueryClientProvider>
  );
};

const App = () => {
  const isAuthenticated = useIsAuthenticated();

  return (
    <ChakraProvider theme={theme}>
      {isAuthenticated ? (
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={{}}
          loadingComponent={LoadingComponent}
        >
          <AuthenticatedApp />
        </MsalAuthenticationTemplate>
      ) : (
        <UnauthenticatedTemplate>
          <Layout>
            <Router>
              <UnauthenticatedRoutes />
            </Router>
          </Layout>
        </UnauthenticatedTemplate>
      )}

      <ToastContainer />
    </ChakraProvider>
  );
};

export default App;